import {
    AppId,
    FCX,
    ID,
    ProductLogo,
    Tooltip,
    useUuid
} from "@datassential/platform-ui-lib";
import { FoodSectionEnum } from "@pages/Food/Food.constants";
import FoodSection from "@pages/Food/components/Section/Section";
import { AiOutlineInfoCircle } from "react-icons/ai";
import cn from "classnames";
import Widget from "@components/Widget/Widget";
import FoodTabs from "@pages/Food/components/Tabs/Tabs";
import './SectionSocial.scss';
import FoodSocialMentions from "@pages/Food/components/SectionSocial/components/SocialMentions/SocialMentions";
import FoodSocialAssociations
    from "@pages/Food/components/SectionSocial/components/SocialAssociations/SocialAssociations";
import { PermissionLevelEnum } from "@models/PermissionSection";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import FoodSocialPosts from "@pages/Food/components/SectionSocial/components/Posts/Posts";
import useNotification from "@hooks/useNotification";
import CopyButton from "@components/new/Table/CopyButton";
import CopyNotification from "@components/new/Table/CopyNotification";
import { FoodSocialMetricEnum } from "@models/FoodSocial";
import { FoodSocialSortFilterEnum } from "@pages/Food/components/SectionSocial/hooks/useFoodSocialSection.constants";
import useFoodSocialSection from "@pages/Food/components/SectionSocial/hooks/useFoodSocialSection";

/** Social section of the legacy Food Profile (flag off). State lives in the shared `useFoodSocialSection`. */
const FoodSectionSocial: FCX<{
    word: string;
    wordId: ID;
    setHasData: (hasData: boolean) => void;
}> = ({
    word,
    wordId,
    setHasData,
}) => {
    const notification = useNotification();
    const dispatch = useDispatch();
    const requestTrial: (f: string, hideNotification?: boolean) => any = (featureName, hideNotification) => {
        if (!hideNotification) {
            toast.info('Request has been sent');
        }
        return dispatch(requestTrialByFeature(featureName));
    };

    const {
        isLoading,
        hasData,
        permissionLevel,
        data,
        isMinimalFiltersFeatureEnabled,
        isCustomAssociationEnabled,
        selectedMetric,
        setSelectedMetric,
        selectedSortFilterMetric,
        setSelectedSortFilterMetric,
        selectedMonth,
        selectedYear,
        selectedAssociation,
        selectedCustomAssociation,
        onMentionBarClick,
        onAssociationBarClick,
        onCustomAssociationClick,
        clearAssociations,
        associationLabel,
        postsData,
        postsDateRangeString,
        sortedByString,
        copyPostsData,
        postsTabs,
        postsSectionKey,
        isPostsLoading,
    } = useFoodSocialSection({ wordId, setHasData });
    const tooltipId = useUuid();
    const tabsTooltipId = useUuid();

    const hasAccess = permissionLevel === PermissionLevelEnum.Full;

    if (isLoading) {
        return (
            <div className="Food__section is-loading">
                <div className="spinner spinner-blink">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            </div>
        );
    }

    if (!hasData) return null;

    return (
        <div
            className="Food__section appears-top del-1"
            id={FoodSectionEnum.Social}
            data-testid="blockFoodSectionSocial"
        >
            <FoodSection
                testId="blockFoodSocial"
                className={cn(
                    "FoodSectionSocial",
                    permissionLevel !== PermissionLevelEnum.Full && 'is-blocked',
                    isCustomAssociationEnabled && 'is-with-custom-association',
                )}
                headerContent={(
                    <div data-testid="textFoodSocialTitle" className="FoodSectionSocial__section-title">
                        SOCIAL&nbsp;
                        <AiOutlineInfoCircle data-tooltip-id={tooltipId}/>&nbsp;&nbsp;
                        <div
                            data-testid="textFoodSocialLegend"
                            className="Widget__header-sub-title"
                            style={{
                                transform: 'translateY(2px)',
                            }}
                        >
                            CLICK BARS TO SEE EXAMPLES
                        </div>
                    </div>
                )}
            >
                <div className="FoodSection__row">
                    <Widget
                        headerContent={(<>
                            <div
                                className="FoodSectionSocial__widget-title"
                                data-testid="textFoodSocialTotalMentionsTitle"
                            >
                                TOTAL MENTIONS
                                <div
                                    className="Widget__header-sub-title ml"
                                    data-testid="textFoodSocialTotalMentionsSubTitle"
                                >
                                    &nbsp;past 24 months
                                </div>
                            </div>
                            <FoodTabs
                                data-tooltip-id={tabsTooltipId}
                                data={[
                                    { id: FoodSocialMetricEnum.Mentions, title: FoodSocialMetricEnum.Mentions },
                                    { id: FoodSocialMetricEnum.Engagement, title: FoodSocialMetricEnum.Engagement },
                                ]}
                                activeTabId={selectedMetric}
                                onItemClick={(id) => setSelectedMetric(id as FoodSocialMetricEnum)}
                                testId="blockFoodSocialTotalMentionsTabs"
                            />
                        </>)}
                        className="FoodSectionSocial__widget-1"
                    >
                        <FoodSocialMentions
                            selectedMetric={selectedMetric}
                            socialData={data}
                            selectedMonth={selectedMonth}
                            selectedYear={selectedYear}
                            onMentionBarClick={onMentionBarClick}
                        />
                    </Widget>
                    <div className="FoodSectionSocial__widget-2">
                        <FoodSocialAssociations
                            word={word}
                            wordId={wordId}
                            selectedAssociation={selectedAssociation}
                            foodAssociations={data.foodAssociations}
                            placesAssociations={data.placesAssociations}
                            onItemClick={onAssociationBarClick}
                            isCustomAssociationEnabled={isCustomAssociationEnabled}
                            selectedCustomAssociationQuery={selectedCustomAssociation?.query}
                            onCustomAssociationClick={onCustomAssociationClick}
                        />
                    </div>
                </div>
                <div className="FoodSection__row">
                    <Widget
                        headerContent={(<>
                            <div
                                className="FoodSectionSocial__widget-title"
                                data-testid="textFoodSocialExamplePostsTitle"
                            >
                                EXAMPLE POSTS:&nbsp;{associationLabel && <>
                                <b>{associationLabel}</b>&nbsp;+&nbsp;</>}{word}
                                <div
                                    className="Widget__header-sub-title ml"
                                    data-testid="textFoodSocialExamplePostsSubTitle"
                                >
                                    &nbsp;{sortedByString},&nbsp;{postsDateRangeString}
                                </div>
                            </div>
                            <div className="FoodSectionSocial__widget-title">
                                <FoodTabs
                                    data={postsTabs}
                                    activeTabId={selectedSortFilterMetric}
                                    onItemClick={(id) => setSelectedSortFilterMetric(id as FoodSocialSortFilterEnum)}
                                    testId="blockFoodSocialExamplePostsTabs"
                                    className="FoodSectionSocial__tabs"
                                />
                                {!isMinimalFiltersFeatureEnabled && (
                                    <div
                                        className="FoodSectionSocial__mini-btn"
                                        data-testid="buttonFoodSocialExamplePostsClearSortBy"
                                        onClick={clearAssociations}
                                    >
                                        clear
                                    </div>
                                )}
                                <div onClick={notification.handleShowNotification}>
                                    <CopyButton
                                        testId="buttonCopyTable"
                                        data={copyPostsData}
                                    />
                                </div>
                            </div>
                        </>)}
                        className="FoodSectionSocial__widget"
                    >
                        <FoodSocialPosts
                            key={postsSectionKey}
                            data={postsData}
                            word={word}
                            association={associationLabel}
                            isLoading={isPostsLoading}
                        />
                        <CopyNotification isOpen={notification.isOpen}>
                            <span>Data successfully copied</span>
                        </CopyNotification>
                    </Widget>
                </div>
            </FoodSection>
            {!hasAccess && (
                <PermissionOverlay
                    onContactUsClick={() => requestTrial('social')}
                    testId="blockFoodSummaryPermissionOverlay"
                >
                    <div data-testid="textFoodSummaryPermissionOverlayContentLine-0">
                        Upgrade your subscription to unlock
                    </div>
                    <div data-testid="textFoodSummaryPermissionOverlayContentLine-1">
                        insights about <b>{word}</b>
                    </div>
                </PermissionOverlay>
            )}
            <Tooltip
                id={tooltipId}
                place="bottom-start"
            >
                <div className="FoodSectionSocial__tooltip raw-html">
                    <div style={{ marginBottom: 5 }}><b>Data Details</b></div>
                    <ul>
                        <li>updated weekly</li>
                        <li>cleaned and vetted by Datassential trend experts</li>
                        <li>sources include: TikTok, Instagram, Pinterest, YouTube, X, Reddit</li>
                        <li>reflects US & Canadian social activity</li>
                    </ul>
                </div>
            </Tooltip>
            <Tooltip
                id={tabsTooltipId}
                place="bottom"
            >
                <div style={{ width: 170 }}>
                    <div><b>Mentions</b></div>
                    <div>count of total mentions of this term</div>
                    <br/>
                    <div><b>Engagement</b></div>
                    <div>sum of likes, comment, and shares</div>
                </div>
            </Tooltip>
        </div>
    );
};

export default FoodSectionSocial;